/**
 * Action-level permission gating.
 *
 * Screens are gated by RequirePermission in AdminRoutes; this is the same idea
 * one level down, for the buttons inside a screen. Before it, no page gated any
 * action at all, so a Billing operator saw a "Create Tenant" button that the
 * API would refuse.
 *
 * There is no second permission model here and no role names anywhere in this
 * file. `can(permission)` asks the AdminAuthContext, which holds whatever
 * /api/admin/capabilities returned, which the backend computed from
 * shared/constants/operator_permissions.py. Adding a role or moving a
 * permission between roles requires no frontend change at all.
 *
 * Two presentations, per the stated preference:
 *
 *   disable (default) — the control stays visible and explains itself. Use when
 *     knowing the action exists is useful: the operator learns what the screen
 *     can do and what to ask an Owner for.
 *   hide              — the control is not rendered. Use only when showing it
 *     would mislead (an action that is irrelevant to this operator's job) or
 *     when its mere presence discloses something.
 *
 * Hiding is never the security control. Every action behind these buttons is
 * enforced server-side by require_operator_permission, so a disabled button
 * that someone re-enables in devtools still gets a 403.
 */
import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { useAdminAuth } from '../auth/AdminAuthContext';

/**
 * Whether the signed-in operator holds a permission, plus a sentence saying why
 * not. Fails closed: while capabilities are loading, `allowed` is false.
 */
export const usePermission = (permission) => {
  const { can, capabilitiesLoaded, capabilityError } = useAdminAuth();
  const allowed = !permission || can(permission);

  let reason = null;
  if (!allowed) {
    if (capabilityError) {
      reason = 'Your permissions could not be loaded. Reload to retry.';
    } else if (!capabilitiesLoaded) {
      reason = 'Checking your permissions…';
    } else {
      reason = `Requires the ${permission} permission. Ask an Owner if you need it.`;
    }
  }
  return { allowed, reason };
};

/**
 * Wrap a control that requires a permission.
 *
 * The child is cloned with `disabled` when refused, so it works with any
 * control that accepts `disabled` — Button, MenuItem, Switch — without this
 * component knowing which. An already-disabled child stays disabled.
 */
export const PermissionGate = ({
  permission, mode = 'disable', children, wrapperClassName, tooltip = true,
}) => {
  const { allowed, reason } = usePermission(permission);

  if (allowed) return children;
  if (mode === 'hide') return null;

  const disabledChild = React.cloneElement(children, {
    disabled: true,
    // Native title, so the reason survives even if the element is inside
    // something that swallows pointer events on disabled children.
    title: reason,
  });

  // Inside a Radix menu the extra <span> the tooltip trigger needs breaks
  // keyboard navigation and item styling, so menu items opt out and rely on
  // the native title instead. Same refusal, same reason text, no wrapper.
  if (!tooltip) return disabledChild;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        {/* A disabled button emits no pointer events, so the trigger needs a
            wrapper element of its own or the tooltip never opens. */}
        <TooltipTrigger asChild>
          <span className={wrapperClassName ?? 'inline-flex cursor-not-allowed'}>
            {disabledChild}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">{reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default PermissionGate;
